import type { TutorBillingListData } from '../model/interfaces'
import type { ApiResponse } from '@shared/api'
import type { RouteLocationNormalizedGeneric } from 'vue-router'

import { useAdminNow } from '@entities/viewer'
import { useCommonStore } from '@shared/model'
import { getAxiosErrorMessage } from '@shared/api'
import { pick } from '@shared/lib/collections'
import { MAX_PER_PAGE, toPaginationValue, useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

import { getTutorBillingFilterParams } from '../lib/tutor-billing-query'
import { TUTOR_BILLING_DEFAULT_PER_PAGE } from '../config/tutor-billing'
import { useTutorBillingStore } from '../model/store'

interface TutorBillingListApiOptions {
  route?: RouteLocationNormalizedGeneric
}

let requestSeq = 0
let activeController: AbortController | null = null

const isCanceled = (err: unknown): boolean => (err as Error)?.name === 'CanceledError'

export const useTutorBillingListApi = async (options?: TutorBillingListApiOptions): Promise<TutorBillingListData | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const route = options?.route || useRoute()
  const store = useTutorBillingStore()
  const commonStore = useCommonStore()

  const url = useAdminRoutes('admin.tutors.billing.list', pick({
    ...getTutorBillingFilterParams(store.getFilters, useAdminNow()),
    per_page: toPaginationValue(route.query?.per_page, TUTOR_BILLING_DEFAULT_PER_PAGE, MAX_PER_PAGE),
    page: toPaginationValue(route.query?.page, store.getCurrentPage),
  }))

  const seq = ++requestSeq
  const isLatest = (): boolean => seq === requestSeq

  activeController?.abort()
  const controller = new AbortController()
  activeController = controller

  store.setLoading(true)
  commonStore.setLoading(true)

  try {
    if (!url) {
      toast.error('Unexpected error. Invalid request URL.')
      return null
    }

    const { data } = await $axios.get<ApiResponse>(url, { signal: controller.signal })

    if (!isLatest()) return null

    if (!data.success) {
      toast.error(data.message)
      return null
    }

    const listData: TutorBillingListData | null = data.data || null

    if (listData) {
      store.setRows(listData.billings)
      store.setMeta(listData.meta)
    }

    return listData
  } catch (err: unknown) {
    if (isLatest() && !isCanceled(err)) toast.error(getAxiosErrorMessage(err))
    return null
  } finally {
    if (isLatest()) {
      store.setLoading(false)
      commonStore.setLoading(false)
      activeController = null
    }
  }
}
